import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  sendUserConfigAction,
  updateUserConfigAction,
} from 'root-redux/actions/user'
import { selectTimerEndTimes } from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { useCohortInfo } from 'hooks/useCohortInfo'
import { useGetPageInfo } from 'hooks/useGetPageInfo'

import { eventLogger } from 'services/eventLogger.service'

import {
  MILLISECONDS_IN_SECOND,
  MINUTES_IN_SECONDS,
  SubscriptionListType,
  SubscriptionTagsType,
} from 'root-constants'

import { COHORTS_WITH_PRICE_UPDATE_ON_TIMER_END } from '../constants'

type TParams = {
  baseSubscriptionTags: string
  subscriptionType: SubscriptionListType
  isCheckoutOpen: boolean
}

type TReturn = {
  onTimerComplete: () => void
  shouldShowTimer: boolean
  subscriptionTags: string
  timerTimeInSeconds: number
}

export const usePaywallTimerPriceTags = ({
  baseSubscriptionTags,
  subscriptionType,
  isCheckoutOpen,
}: TParams): TReturn => {
  const dispatch: TAppDispatch = useDispatch()

  const timerEndTimes = useSelector(selectTimerEndTimes)
  const { cohortToUse } = useCohortInfo()
  const { currentPageId } = useGetPageInfo()

  const isPaywallTimerPriceCohort =
    COHORTS_WITH_PRICE_UPDATE_ON_TIMER_END.includes(cohortToUse)

  const timerKey = [currentPageId, cohortToUse, subscriptionType].join(':')

  const [mountTime] = useState(() => Date.now())
  const [initialEndTimes] = useState(() => timerEndTimes)

  const storedEndTime = initialEndTimes[timerKey]
  const endTime =
    storedEndTime ?? mountTime + MINUTES_IN_SECONDS.TEN * MILLISECONDS_IN_SECOND
  const timerTimeInSeconds = Math.max(
    0,
    Math.round((endTime - mountTime) / MILLISECONDS_IN_SECOND),
  )

  const [isTimerCompleted, setIsTimerCompleted] = useState(false)
  const [isExpiryPending, setIsExpiryPending] = useState(false)

  const wasExpiredOnMount = timerTimeInSeconds <= 0

  const hasTimerExpired =
    isPaywallTimerPriceCohort && (wasExpiredOnMount || isTimerCompleted)

  const subscriptionTags = hasTimerExpired
    ? `${SubscriptionTagsType.FULL_OFFER},${baseSubscriptionTags}`
    : baseSubscriptionTags

  useEffect(() => {
    if (!isPaywallTimerPriceCohort || storedEndTime !== undefined) return

    const nextEndTimes = { ...initialEndTimes, [timerKey]: endTime }

    dispatch(updateUserConfigAction({ timerEndTimes: nextEndTimes }))
    dispatch(sendUserConfigAction({ timer_end_times: nextEndTimes }))
  }, [
    dispatch,
    endTime,
    initialEndTimes,
    isPaywallTimerPriceCohort,
    storedEndTime,
    timerKey,
  ])

  const onTimerComplete = useCallback(() => {
    if (wasExpiredOnMount || isTimerCompleted || isExpiryPending) return

    eventLogger.logPlanPageTimerExpired(currentPageId)

    if (isCheckoutOpen) {
      setIsExpiryPending(true)
      return
    }

    setIsTimerCompleted(true)
  }, [
    currentPageId,
    wasExpiredOnMount,
    isCheckoutOpen,
    isTimerCompleted,
    isExpiryPending,
  ])

  useEffect(() => {
    if (!isExpiryPending || isCheckoutOpen) return

    setIsTimerCompleted(true)
    setIsExpiryPending(false)
  }, [isExpiryPending, isCheckoutOpen])

  return {
    onTimerComplete,
    shouldShowTimer: isPaywallTimerPriceCohort,
    subscriptionTags,
    timerTimeInSeconds,
  }
}
